import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import { SupplierPayment, SupplierPaymentStatus } from '../../interfaces/procurement/supplierPayment.interface';
import { useToast } from '../../context/ToastContext';

const STATUS_COLORS: Record<SupplierPaymentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  POSTED: 'success',
  REVERSED: 'error',
};

export const SupplierPaymentList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const supplierBillId = searchParams.get('supplierBillId');

  const [payments, setPayments] = useState<SupplierPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const fetchPayments = async (page = 0) => {
    if (!supplierBillId) return;
    setLoading(true);
    try {
      const response = await SupplierPaymentService.listByBill(supplierBillId, page, 10);
      setPayments(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(0);
  }, [supplierBillId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchPayments(page - 1);
  };

  if (!supplierBillId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Supplier Payments</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Open a Supplier Bill to view its payments.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/procurement/supplier-bills')}>
          Go to Supplier Bills
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Payments for Bill</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/procurement/supplier-payments/new?supplierBillId=${supplierBillId}`)}
          >
            Record Payment
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Payment Number</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map(payment => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.paymentNumber}</TableCell>
                    <TableCell>{payment.paymentDate}</TableCell>
                    <TableCell>{payment.amount}</TableCell>
                    <TableCell>
                      <Chip label={payment.status} color={STATUS_COLORS[payment.status]} size="small" />
                    </TableCell>
                    <TableCell>{payment.paymentMethod ?? '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="View" arrow>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/procurement/supplier-payments/${payment.id}`)}
                          aria-label="View"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageInfo.totalPages}
                page={pageInfo.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SupplierPaymentList;
